import { useEffect, useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import { KeyRound, CheckCircle2, ArrowLeft } from "lucide-react";
import { AccentButton, AuthScene, Button, Input, Label, Loader } from "@wa/ui";
import type { AuthLayoutSpec } from "@wa/core/theme";
import { useT } from "../i18n/I18nProvider";
import { resetPassword, resetTokenStatus } from "../lib/auth";
import { Logo } from "../components/Logo";
import { AppBackground } from "../components/AppBackground";
import { LanguageMenu } from "../components/LanguageMenu";

/**
 * Parooli-lähtestuse maandumisleht - e-kirja lingi (`/reset-password?token=…`)
 * siht. Töötab välja logituna. Kontrollib tokeni kehtivust, laseb seada uue
 * parooli, seejärel suunab sisselogimisse. `?mode=set` = „määra parool" voog
 * (OAuth-ainult kasutaja lisab kontole esimese parooli) - sama mehhanism,
 * erinev sõnastus; backend säästis tema käesoleva sessiooni.
 */
export function ResetPasswordPage({
  onDone,
  authSpec,
}: {
  onDone: () => void;
  authSpec?: AuthLayoutSpec;
}) {
  const t = useT();
  const glow = authSpec?.glow ?? "aurora";

  const search = new URLSearchParams(window.location.search);
  const token = search.get("token") ?? "";
  const isSetup = search.get("mode") === "set";
  const k = (base: string) => t(isSetup ? `reset.set.${base}` : `reset.${base}`);
  const [status, setStatus] = useState<"checking" | "valid" | "invalid" | "done">("checking");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    resetTokenStatus(token).then((valid) => setStatus(valid ? "valid" : "invalid"));
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password.length < 8) {
      setErr(t("reset.tooShort"));
      return;
    }
    if (password !== confirm) {
      setErr(t("reset.mismatch"));
      return;
    }
    setBusy(true);
    try {
      await resetPassword(token, password);
      setStatus("done");
    } catch (e) {
      // Token vahepeal aegunud / juba kasutatud → suuna „taotle uus".
      const code = e instanceof Error ? e.message : "";
      if (code === "invalid_or_expired") setStatus("invalid");
      else setErr(t("reset.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthScene
      layout={authSpec?.layout}
      background={<AppBackground spec={authSpec} />}
      logo={<Logo variant="full" className="h-11 w-auto text-foreground md:h-12" />}
      topRight={<LanguageMenu />}
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="card-aurora-border rounded-xl bg-card/95 p-6 shadow-soft">
          {status === "checking" ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <Loader size="md" />
              <p className="text-sm text-muted-foreground">{t("reset.checking")}</p>
            </div>
          ) : status === "invalid" ? (
            <div className="flex flex-col items-center gap-4 py-2 text-center">
              <KeyRound className="size-8 text-destructive" />
              <h1 className="text-xl font-bold tracking-tight">{k("title")}</h1>
              <p className="text-sm text-destructive">{t("reset.invalid")}</p>
              <Button type="button" variant="outline" size="md" className="w-full" onClick={onDone}>
                {t("reset.requestNew")}
              </Button>
            </div>
          ) : status === "done" ? (
            <div className="flex flex-col items-center gap-4 py-2 text-center">
              <CheckCircle2 className="size-8 text-primary" />
              <h1 className="text-xl font-bold tracking-tight">{k("title")}</h1>
              <p className="text-sm text-muted-foreground">{k("success")}</p>
              <AccentButton glow={glow} type="button" size="md" className="w-full" onClick={onDone}>
                {k("toLogin")}
              </AccentButton>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <KeyRound className="size-8 text-primary" />
                <h1 className="text-xl font-bold tracking-tight">{k("title")}</h1>
                <p className="text-sm text-muted-foreground">{k("subtitle")}</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-password">{t("reset.password")}</Label>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">{t("reset.confirm")}</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </div>
              {err && <p className="text-sm text-destructive">{err}</p>}
              <AccentButton glow={glow} type="submit" size="md" loading={busy} className="w-full">
                {busy ? t("reset.busy") : t("reset.submit")}
              </AccentButton>
              <Button type="button" variant="ghost" size="sm" onClick={onDone} className="self-center">
                <ArrowLeft className="mr-1 size-4" />
                {k("toLogin")}
              </Button>
            </form>
          )}
        </div>
      </motion.div>
    </AuthScene>
  );
}
